import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { useSessionUser } from '../SessionContext';
import { buildAgentStatePayload, sendAgentStateBeacon, sendAgentStateLog } from '../lib/agentStateLog';
import { unregisterAgentPresence } from '../lib/agentPresence';
import api from '../services/api';

const LOGOUT_PENDING_KEY = 'cms.logout.pending';
const TAB_ID_KEY = 'cms.tabId';
const TAB_NAME_PREFIX = 'cms-tab:';
const DEFAULT_NOTICE = 'Session ended because the app was closed.';
const PENDING_RECHECK_MS = 800;
const SESSION_STATUS_INTERVAL_MS = 15000;
const NAV_TYPE_RELOAD = 'reload';
const NAV_TYPE_BACK_FORWARD = 'back_forward';

const readWindowTabId = () => {
  if (typeof window === 'undefined') return '';
  const name = String(window.name || '');
  if (name.startsWith(TAB_NAME_PREFIX)) {
    return name.slice(TAB_NAME_PREFIX.length);
  }
  return '';
};

const writeWindowTabId = (tabId) => {
  if (typeof window === 'undefined') return;
  try {
    if (!window.name || String(window.name).startsWith(TAB_NAME_PREFIX)) {
      window.name = `${TAB_NAME_PREFIX}${tabId}`;
    }
  } catch {
    // Ignore window name errors.
  }
};

const getTabId = () => {
  try {
    const stored = sessionStorage.getItem(TAB_ID_KEY);
    if (stored) return stored;
  } catch {
    // Ignore sessionStorage errors.
  }

  const fromName = readWindowTabId();
  if (fromName) {
    try {
      sessionStorage.setItem(TAB_ID_KEY, fromName);
    } catch {
      // Ignore sessionStorage errors.
    }
    return fromName;
  }

  const fallback = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const generated = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : fallback;
  try {
    sessionStorage.setItem(TAB_ID_KEY, generated);
  } catch {
    // Ignore sessionStorage errors.
  }
  writeWindowTabId(generated);
  return generated;
};

const readPendingLogout = () => {
  try {
    const raw = localStorage.getItem(LOGOUT_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.tabId || !parsed.ts) return null;
    return parsed;
  } catch {
    return null;
  }
};

const markLogoutPending = (tabId) => {
  try {
    localStorage.setItem(
      LOGOUT_PENDING_KEY,
      JSON.stringify({ tabId, ts: Date.now() })
    );
  } catch {
    // Ignore storage errors.
  }
};

const clearLogoutPending = () => {
  try {
    localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch {
    // Ignore storage errors.
  }
};

const clearClientStorage = () => {
  try {
    localStorage.clear();
  } catch {
    // Ignore storage errors.
  }
  try {
    sessionStorage.clear();
  } catch {
    // Ignore storage errors.
  }
};

const isReloadNavigation = () => {
  if (typeof performance === 'undefined') return false;
  const entries = performance.getEntriesByType?.('navigation');
  if (entries && entries.length) {
    const type = entries[0]?.type;
    return type === NAV_TYPE_RELOAD || type === NAV_TYPE_BACK_FORWARD;
  }
  // Fallback for older browsers (performance.navigation is deprecated)
  if (performance.navigation && typeof performance.navigation.type === 'number') {
    return performance.navigation.type === 1; // TYPE_RELOAD
  }
  return false;
};

const logAgentLogout = (useBeacon) => {
  const payload = buildAgentStatePayload({
    status: 'logout',
    reason: 'logout',
    inQueue: '0'
  });
  if (!payload) return;
  if (useBeacon) {
    sendAgentStateBeacon(payload);
    return;
  }
  sendAgentStateLog(payload).catch(() => {});
};

export default function LogoutSync() {
  const navigate = useNavigate();
  const { disconnect, hangup } = useSoftphone();
  const { clearUser } = useSessionUser();
  const logoutRanRef = useRef(false);
  const pendingTimerRef = useRef(null);
  const tabIdRef = useRef(getTabId());

  const clearPendingTimer = useCallback(() => {
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }, []);

  const performLogout = useCallback(
    ({ sendBeacon = false, navigateToLogin = true, notice = '' } = {}) => {
      if (logoutRanRef.current) return;
      logoutRanRef.current = true;

      logAgentLogout(sendBeacon);
      console.info('[LogoutSync] Logout triggered');

      try {
        hangup();
      } catch {
        // Ignore hangup errors on teardown.
      }
      unregisterAgentPresence().catch(() => {});
      try {
        disconnect().catch(() => {});
      } catch {
        // Ignore disconnect errors on teardown.
      }
      try {
        clearUser();
      } catch {
        // Ignore session errors on teardown.
      }

      clearClientStorage();

      if (navigateToLogin) {
        navigate('/login', {
          replace: true,
          state: notice ? { logoutNotice: notice } : {}
        });
      }
    },
    [clearUser, disconnect, hangup, navigate]
  );

  useEffect(() => {
    const handleSessionRevoked = (event) => {
      performLogout({
        sendBeacon: false,
        navigateToLogin: true,
        notice: event?.detail?.message ||
          'This session was signed out because the user logged in elsewhere.'
      });
    };
    const checkSession = () => {
      if (!localStorage.getItem('token')) return;
      logoutRanRef.current = false;
      api.get('/session/status').catch(() => {});
    };

    window.addEventListener('cms:session-revoked', handleSessionRevoked);
    checkSession();
    const intervalId = setInterval(checkSession, SESSION_STATUS_INTERVAL_MS);

    return () => {
      window.removeEventListener('cms:session-revoked', handleSessionRevoked);
      clearInterval(intervalId);
    };
  }, [performLogout]);

  useEffect(() => {
    const pending = readPendingLogout();
    if (!pending) return;
    if (isReloadNavigation()) {
      clearLogoutPending();
      return;
    }
    if (pending.tabId === tabIdRef.current) {
      clearLogoutPending();
      return;
    }
    clearPendingTimer();
    pendingTimerRef.current = setTimeout(() => {
      const latest = readPendingLogout();
      if (!latest) return;
      if (latest.tabId === tabIdRef.current) {
        clearLogoutPending();
        return;
      }
      performLogout({
        sendBeacon: false,
        navigateToLogin: true,
        notice: DEFAULT_NOTICE
      });
    }, PENDING_RECHECK_MS);
  }, [clearPendingTimer, performLogout]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.storageArea !== localStorage) return;
      if (event.key === LOGOUT_PENDING_KEY) {
        if (!event.newValue) {
          clearPendingTimer();
          return;
        }
        const pending = readPendingLogout();
        if (pending && pending.tabId !== tabIdRef.current) {
          clearLogoutPending();
        }
        return;
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [clearPendingTimer]);

  useEffect(() => {
    const handleClose = () => {
      markLogoutPending(tabIdRef.current);
    };

    window.addEventListener('pagehide', handleClose);

    return () => {
      window.removeEventListener('pagehide', handleClose);
    };
  }, []);

  return null;
}
